import { LogContext, MetaAttributes } from '@grafana/faro-react';
import React, { createContext, PropsWithChildren, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { useBroadcastJsLoggerEvents } from '../hooks/useBroadcastJsLoggerEvents';
import { useLoggerService } from '../hooks/useLoggerService';
import { useOfflineRetry } from '../hooks/useOfflineRetry';
import { BeforeSendEvent, LoggerConfig, LoggerType, LogLevel } from '../types';

import { NetworkStatusProvider, useNetworkStatus } from './NetworkStatusContext';
import { initializeLogger } from './initializeLogger';
import { beforeSend } from './utils';

const LoggerContext = createContext<LoggerType | null>(null);

interface LoggerComponent {
  config: LoggerConfig;
  sessionContext?: MetaAttributes;
  children: ReactNode;
}

// Internal component that handles the actual logging logic
const LoggerImplementation = ({ config, sessionContext, children }: PropsWithChildren<LoggerComponent>) => {
  const { app, offlineConfig, loggerOutput, broadcastJsLoggerEvents } = config;
  const { isOnline } = useNetworkStatus();
  const [isInitialized, setIsInitialized] = useState(false);
  const [pendingLogs, setPendingLogs] = useState<
    {
      level: LogLevel;
      message: string;
      context?: LogContext;
    }[]
  >([]);
  const isRemoteLogger = loggerOutput === 'remote';

  // The Grafana Faro logger must be initialized before its api is used
  useEffect(() => {
    if (!isInitialized && isRemoteLogger) {
      initializeLogger({
        beforeSend: (event) => beforeSend(event as BeforeSendEvent, sessionContext),
        app: {
          name: app.name,
          version: app.version,
          environment: app.environment
        },
        url: config.url,
        instrumentations: config.instrumentations
      });
      setIsInitialized(true);
    }
  }, [
    app,
    offlineConfig,
    loggerOutput,
    isRemoteLogger,
    isInitialized,
    config.url,
    config.instrumentations,
    sessionContext
  ]);

  // A log will either be sent to the remote logger or the local console
  const { processLog } = useLoggerService({
    isInitialized,
    isRemoteLogger,
    provider: config.loggerOutput
  });

  // If the user is offline, the log will be queued for sending when the user comes back online
  const { queueLog } = useOfflineRetry({
    isOnline,
    maxRetries: offlineConfig?.maxRetries ?? 3,
    retryDelay: offlineConfig?.retryDelay ?? 5000,
    processLog
  });

  // Flush pending logs when initialized (only for remote logger)
  useEffect(() => {
    if (isRemoteLogger && isInitialized && pendingLogs.length > 0) {
      pendingLogs.forEach(({ level, message, context }) => {
        try {
          processLog(level, message, context);
        } catch (error) {
          console.warn('Failed to process buffered log, queueing for retry:', error);
          queueLog(level, message, context);
        }
      });
      setPendingLogs([]);
    }
  }, [isRemoteLogger, isInitialized, pendingLogs, processLog, queueLog]);

  const handleLog = useMemo(
    () => (level: LogLevel, message: string, context?: LogContext) => {
      // Suppress all logging if provider is 'none'
      if (config.loggerOutput === 'none') {
        return;
      }

      if (isRemoteLogger && !isInitialized) {
        setPendingLogs((logs) => [...logs, { level, message, context }]);
        return;
      }

      if (!isOnline) {
        queueLog(level, message, context);
        return;
      }

      try {
        processLog(level, message, context);
      } catch (error) {
        console.warn('Failed to process log, queueing for retry:', error);
        queueLog(level, message, context);
      }
    },
    [config.loggerOutput, isRemoteLogger, isInitialized, isOnline, processLog, queueLog]
  );

  const contextValue = useMemo(
    () => ({
      log: (message: string, context?: LogContext) => handleLog('log', message, context),
      info: (message: string, context?: LogContext) => handleLog('info', message, context),
      warn: (message: string, context?: LogContext) => handleLog('warn', message, context),
      error: (message: string, context?: LogContext) => handleLog('error', message, context)
    }),
    [handleLog]
  );

  useBroadcastJsLoggerEvents({
    onJsLogReceived: (message, level, context) => {
      handleLog(level, message, context as LogContext);
    },
    enabled: broadcastJsLoggerEvents
  });

  return <LoggerContext.Provider value={contextValue}>{children}</LoggerContext.Provider>;
};

export const LoggerProvider = ({ config, sessionContext = {}, children }: PropsWithChildren<LoggerComponent>) => {
  return (
    <NetworkStatusProvider>
      <LoggerImplementation config={config} sessionContext={sessionContext}>
        {children}
      </LoggerImplementation>
    </NetworkStatusProvider>
  );
};

export const useLogger = (): LoggerType => {
  const context = useContext(LoggerContext);
  if (!context) {
    throw new Error('useLogger must be used within a LoggerProvider');
  }
  return context;
};
